<template>
  <div
    class="element border5 overflow-ellipsis"
    :data-testid="`layer-${layerId}`"
    :class="{ 'active-background': layer?.active.value }"
    :style="{ color: theme === 'light' ? 'black' : 'white' }">
    <div class="d-flex align-center ma-1 bg-accent pa-1 rounded">
      <div class="d-flex align-center">
        <img v-if="layer?.rawData.customProperties?.img" class="rounded" :src="String(layer?.rawData.customProperties.img)" width="50" />
        <v-icon v-else class="px-2">
          {{ layer?.icon }}
        </v-icon>
      </div>
      <div class="pointer" data-testid="layer-title">
        {{ translateLangsKeyObject(layer?.rawData.title) }}
        <v-tooltip activator="parent" location="bottom">
          {{ translateLangsKeyObject(layer?.rawData.title) }}
        </v-tooltip>
      </div>

      <v-progress-circular v-if="isUpdating" indeterminate color="accent" size="small" />
      <div class="d-flex align-center justify-end">
        <v-tooltip v-if="isHiddenByZoom" location="top">
          <template #activator="{ props: tooltipProps }">
            <v-icon v-bind="tooltipProps" color="rgb(var(--v-theme-lighten))" size="small" class="mr-1">mdi-magnify-plus-outline</v-icon>
          </template>
          {{ $t('messages.info.layerHiddenByZoom') }}
        </v-tooltip>
        <v-menu location="end" :close-on-content-click="false">
          <template #activator="{ props: menuProps }">
            <v-badge
              v-if="layer?.properties.opacity"
              dot
              offset-x="15"
              :model-value="layer?.properties.opacity !== (layer?.rawData.opacity || 1)"
              color="error">
              <v-btn icon variant="text" density="compact" size="small" v-bind="menuProps">
                <v-icon color="rgb(var(--v-theme-lighten))">mdi-opacity</v-icon>
                <v-tooltip activator="parent" location="end">
                  {{ $t('domain.fields.opacity') }}
                </v-tooltip>
              </v-btn>
            </v-badge>
          </template>

          <v-card class="opacity-card">
            <v-card-text class="d-flex">
              <v-slider
                :model-value="layer?.properties.opacity"
                :label="$t('domain.fields.opacity')"
                :min="0"
                :max="1"
                :step="0.01"
                hide-details
                @update:model-value="layer?.setOpacity($event)"></v-slider>
              <v-btn icon="mdi-undo" variant="text" size="small" @click="layer?.setOpacity(layer?.rawData.opacity || 1)"></v-btn>
            </v-card-text>
          </v-card>
        </v-menu>

        <v-btn
          v-if="info"
          icon
          variant="text"
          density="compact"
          size="small"
          :aria-label="$t('common.concepts.info')"
          data-testid="layer-info-button"
          @click="showInfo = true">
          <v-icon color="rgb(var(--v-theme-lighten))">mdi-information</v-icon>
          <v-tooltip activator="parent" location="end">
            {{ $t('common.concepts.info') }}
          </v-tooltip>
        </v-btn>

        <!-- Add a manual reload of the layer -->
        <v-btn
          v-if="canRefresh"
          icon
          variant="text"
          density="compact"
          size="small"
          @click="(layer as Layer & ILayerWithFeatures)?.refresh()">
          <v-icon color="rgb(var(--v-theme-lighten))">mdi-refresh</v-icon>
          <v-tooltip activator="parent" location="end">
            {{ $t('common.actions.refresh') }}
          </v-tooltip>
        </v-btn>

        <v-btn icon variant="text" size="small" density="compact" @click="addRemoveFavorite(layer)">
          <v-icon v-if="isFavorite" color="rgb(var(--v-theme-yellow-color))">mdi-star</v-icon>
          <v-icon v-else>mdi-star-outline</v-icon>
          <v-tooltip activator="parent" location="end">
            {{ $t('common.actions.favorite') }}
          </v-tooltip>
        </v-btn>
        <v-switch
          :data-testid="`layer-switch-${layer?.id}`"
          hide-details
          :model-value="layer?.active.value"
          density="comfortable"
          class="ml-1"
          @update:model-value="layer?.setActive(!!$event)"
          @click="clickOnSwitch(layer)" />
        <v-dialog v-if="info" v-model="showInfo" class="layer-info" scrim="#000">
          <LayerInfo :title="translateLangsKeyObject(layer?.rawData.title)" :info="info" @close="showInfo = false" />
        </v-dialog>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// Framework import
import { computed, ref } from 'vue'

import { storeToRefs } from 'pinia'

import useLayers from '@tsyl/karonte/lib/composables/useLayers'
import type { Layer } from '@tsyl/karonte/lib/types/layers'
import karonteI18n from '@tsyl/karonte/lib/utils/i18n'
import type { ILayerWithFeatures } from '@tsyl/karonte/lib/wrappers/LayerInterfaces'

// Component imports
import favoritesStore from '@/stores/favoritesStore'
import settingsStore from '@/stores/settingsStore.ts'

import LayerInfo from './LayerInfo.vue'

const { theme } = storeToRefs(settingsStore())

const props = defineProps({
  layerId: {
    type: String,
    required: true
  }
})

const emits = defineEmits<{
  (event: 'update', value: Layer): void
}>()

const { allLayers } = useLayers()

const layer = computed(() => {
  return allLayers.value?.find((x) => x.id === props.layerId)
}, {})

const showInfo = ref(false)

const karonteI18nObject = karonteI18n(`sa`, {})

const { translateLangsKeyObject } = karonteI18nObject

const isUpdating = computed(() => {
  return layer?.value?.isUpdating.value
})

const isHiddenByZoom = computed(() => {
  return Boolean(layer.value?.active.value && layer.value.isHiddenFlags.value.scale)
})

const canRefresh = computed(() => {
  return layer.value && 'refresh' in layer.value && typeof layer.value.refresh === 'function'
})

const info = computed(() => layer.value?.rawData.customProperties?.info)

const FavoritesStore = favoritesStore()
const { favorites } = storeToRefs(FavoritesStore)

const isFavorite = computed(() => {
  return favorites.value.find((x) => x === layer?.value?.id)
})

function addRemoveFavorite(layer: Layer | undefined) {
  if (!layer) return
  isFavorite.value
    ? favorites.value.splice(
        favorites.value.findIndex((x) => x === layer?.id),
        1
      )
    : favorites.value.push(layer?.id)
}

function clickOnSwitch(layer: Layer | undefined) {
  if (layer) emits('update', layer)
}
</script>

<style scoped lang="scss">
.element {
  display: flex;
  flex-direction: column;
  margin: 0 1em 0.5em;
  //color: rgb(var(--v-theme-secondary-darken-1));
  background: rgb(var(--v-theme-surface-light));
}

.overflow-ellipsis p {
  width: 143px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.opacity-card {
  width: 250px;
}

.border5 {
  border-radius: 5px;
}

.active-background {
  background-color: rgb(var(--v-theme-primary));
}

.pointer {
  width: min-content;
  padding-left: 4px;
  cursor: pointer;
  flex-grow: 1;
}

.layer-info {
  width: 50em;
  max-height: 90vh;
}
</style>
